// src/components/ProtectedRoute.js
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { Loader2 } from 'lucide-react';

const ProtectedRoute = ({ children }) => {
  const [user, loading] = useAuthState(auth);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [hasWaited, setHasWaited] = useState(false);
  const location = useLocation();

  // Detect if user is on a mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768 && window.innerHeight <= 1024);
  };

  // Give extra time for OAuth redirects and mobile auth to complete
  // This is especially important on mobile where redirects take longer
  // Also need time for mobile-to-web auth token processing
  useEffect(() => {
    // Check if there's an authToken in the URL (mobile auth in progress)
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthToken = urlParams.has('authToken');
    
    // If mobile auth token is present, wait longer to allow authentication to complete
    const minWaitTime = hasAuthToken 
      ? (isMobileDevice() ? 4000 : 3000) // Extra time for mobile auth
      : (isMobileDevice() ? 2000 : 1500); // Normal wait time
    
    const timer = setTimeout(() => {
      setHasWaited(true);
    }, minWaitTime);

    // Also check when loading completes
    if (!loading && hasWaited) {
      // Additional delay after loading completes on mobile
      const finalTimer = setTimeout(() => {
        setCheckingAuth(false);
      }, isMobileDevice() ? 500 : 200);
      
      return () => {
        clearTimeout(timer);
        clearTimeout(finalTimer);
      };
    }

    return () => {
      clearTimeout(timer);
    };
  }, [loading, hasWaited]);

  // Show loader while checking auth state
  // Be more patient on mobile, especially after potential OAuth redirect
  if (loading || checkingAuth || !hasWaited) {
    return (
      <div className="flex justify-center items-center my-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Only redirect if we're absolutely sure there's no user
  // Double-check auth.currentUser as a final verification
  if (!user && !auth.currentUser) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  // If useAuthState says no user but auth.currentUser exists, wait a bit more
  // This handles race conditions with mobile OAuth
  if (!user && auth.currentUser) {
    return (
      <div className="flex justify-center items-center my-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;